
import { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { AdminTabs } from "@/components/admin/AdminTabs";
import { AdminAuthGuard } from "@/components/admin/AdminAuthGuard";
import { UserWithRole } from "@/types/admin";

const queryClient = new QueryClient();

const Admin = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      console.log("Starter hentning af brugere...");
      
      // Hent først alle profiler
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*');

      if (profilesError) {
        console.error('Fejl ved hentning af profiler:', profilesError);
        throw profilesError;
      }

      console.log("Hentede profiler:", profiles);

      // Hent derefter alle brugerroller
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*');

      if (rolesError) {
        console.error('Fejl ved hentning af roller:', rolesError);
        throw rolesError;
      }

      console.log("Hentede roller:", roles);

      // Kombiner profiler med roller
      const usersWithRoles = profiles.map(profile => ({
        ...profile,
        roles: roles.filter(role => role.user_id === profile.id)
      }));

      console.log("Kombinerede brugere med roller:", usersWithRoles);

      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Detaljeret fejl ved indlæsning af brugere:', error);
      toast({
        title: "Fejl ved indlæsning af brugere",
        description: "Der skete en fejl. Prøv igen senere. Fejlbesked: " + (error as Error).message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">
      <p>Indlæser brugere...</p>
    </div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AdminAuthGuard>
        <div className="container mx-auto px-4 py-8">
          <AdminHeader onUserCreated={fetchUsers} />
          <AdminTabs users={users} onRoleUpdate={fetchUsers} />
        </div>
      </AdminAuthGuard>
    </QueryClientProvider>
  );
};

export default Admin;
